import React from "react";
import { Customer } from "../../../core/api/models";
import api from "../../../api";
import { useOutsideClick, Company } from "../../../core";
import { useDataLoader } from "react-remote-data-hooks";
import { DebounceInput } from "react-debounce-input";
import classNames from "classnames";
import { UserAvatar } from "../../../components/render-file/UserAvatar";
import { ContainerLoader } from "../../../components/loader";
import { decodeHtmlEntities } from "../../../utils/common";

interface ISearchCompanyProps {
  onSelect?: (company?: Company, customer?: Customer) => void;
  wraperClassName: string;
  defaultValue?: string;
  setSearchValue: (query?: string) => void;
  onFetchDetails: (data: Customer, query: string, searchType: string) => void;
  setStage: (stage: string) => void;
}

const SearchCompany: React.FunctionComponent<ISearchCompanyProps> = (props) => {
  const [search, setSearch] = React.useState("");
  const [customerSearch, setCustomerSearch] = React.useState("");
  const [showDropDown, setShowDropDown] = React.useState(false);
  const [selectedCompany, setSelectedCompany] = React.useState<Company>();
  const [selectedCompnayId, setSelectedCompanyId] = React.useState<
    Customer[] | null
  >(null);
  const [isCustomerLoaded, setCustomerLoaded] = React.useState(false);

  const [showCustomerDropDown, setShowCustomerDropDown] = React.useState(false);
  const [selectedCustomer, setSelectedCustomer] = React.useState<Customer>();

  const [isSubmiting, setIsSubmiting] = React.useState(false);

  const ref = React.useRef<HTMLDivElement>(null);

  const [containsId, setContainsId] = React.useState<boolean>(false);

  useOutsideClick(ref, () => {
    setShowDropDown(false);
    setShowCustomerDropDown(false);
  });

  
  const { data, loading, loaded, reload } = useDataLoader<{ tenants: Company[] }>(
    async (query?: string) =>
      query
        ? api.company.searchCompanyInline({
            query,
          })
        : [],
    [search]
  );

  const {
    data: customers,
    loading: customersLoading,
    loaded: customersLoaded,
    reload: customersReload,
  } = useDataLoader<Customer[]>(
    async (query?: string, company?: Company) =>
      query ? api.company.searchCustomer(query, company?._id || "") : [],
    [customerSearch, selectedCompany]
  );

  React.useEffect(() => {
    if (loaded) {
      reload(search);
      if (search) {
        setShowDropDown(true);
      }
    }
  }, [search]);

  React.useEffect(() => {
    if (customersLoaded) {
      customersReload(customerSearch, selectedCompany);
      if (customerSearch) {
        setShowCustomerDropDown(true);
      }
    }
  }, [customerSearch, selectedCompany]);

  const onSelect = async (company: Company) => {
    setCustomerLoaded(false);
    setSelectedCompany(company);
    setSearch("");
    setShowDropDown(false);
    const userData = await api.customer.listCorporateCustomers({
      companyId: company._id,
      page: 1,
      perPage: 50,
    });
    setSelectedCompanyId(userData.data);
    setCustomerLoaded(true);
  };

  const onSelectCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    setCustomerSearch("");
    setContainsId(true);
    setShowCustomerDropDown(false);
  };

  const onNext = async () => {
    setIsSubmiting(true);
    const company = await api.company.getDetails(selectedCompany?._id || "");
    let customer;
    if (selectedCustomer) {
      customer = await api.customer.getDetails(selectedCustomer?._id || "");
    }

    props.onSelect?.(company, customer);
    props.setStage(selectedCustomer ? "lead-details" : "add-user");
    setIsSubmiting(false);
  };

  const onAddNewUser = async () => {
    const company = await api.company.getDetails(selectedCompany?._id || "");
    props.onSelect?.(company);
    props.setStage("add-user");
  };

  return (
    <div key={props.defaultValue} ref={ref}>
      <div className="p-4">
        <div className="form-group font-weight-semibold mb-4">
          <label htmlFor="" className="font-sm">
            Company Name
          </label>
          <div className="row gutter-5">
            <div className="col">
              <div className="row border align-items-center no-gutters position-relative">
                <div className="col">
                  <DebounceInput
                    className="form-control rounded-sm font-weight-semibold border-0"
                    minLength={3}
                    debounceTimeout={300}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      props.setSearchValue(e.target.value);
                    }}
                    value={selectedCompany?.name ?? search}
                    onFocus={() => {
                      if (search.length >= 3) {
                        setShowDropDown(true);
                      }
                    }}
                  />
                </div>
                <div className="col-auto">
                  {!loaded || loading ? (
                    <ContainerLoader></ContainerLoader>
                  ) : (
                    <i className="icon-search px-2"></i>
                  )}
                </div>
                {showDropDown && (
                  <div className="custom-dropdown__menu custom-dropdown__menu--open border overflow-auto w-100 position-absolute left-0 bg-white zIndex-1">
                    {data?.tenants?.map((company) => {
                      return (
                        <div
                          onClick={() => onSelect(company)}
                          className="custom-dropdown__menu-item d-flex align-items-center cursor-pointer py-1 mb-1"
                        >
                          <div className="px-2">
                            <div className="font-sm font-weight-semibold">
                              {decodeHtmlEntities(company?.name || "")}
                            </div>
                            <div className="font-sm font-weight-light">
                              {company?.primaryPhone}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div className="custom-dropdown__menu-item align-items-center cursor-pointer py-1 mb-1">
                      <div className="col-md-auto col-12 m-0 p-0">
                        <span
                          className="font-sm font-weight-semibold py-2 px-2 h-100 cursor-pointer text-primary"
                          onClick={() => props.setStage("create-company")}
                        >
                          <i className="icon-plus mr-1"></i> Add New
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {selectedCompany && isCustomerLoaded && (
          <div className="form-group font-weight-semibold mb-4">
            <label htmlFor="" className="font-sm">
              Contact
            </label>
            {selectedCompnayId?.length === 0 ? (
              <p className="mb-0 font-xs text-gray-600 font-weight-semibold">
                No users added. Please add users.
              </p>
            ) : (
              <div className="row no-gutters">
                <div className="col col-12">
                  <div className="border">
                    <ul className="list-unstyled mb-0">
                      {selectedCompnayId?.map((user, index) => {
                        return (
                          <li
                            key={index}
                            className={classNames({
                              "border-bottom px-3 py-2 d-flex align-items-center cursor-pointer align-items-sm-start": true,
                              "bg-white": selectedCustomer?._id !== user._id,
                              "bg-gray-100": selectedCustomer?._id === user._id,
                            })}
                          >
                            <div className="mr-2">
                              <div className="avatar-progress avatar-progress--xs position-relative cursor-pointer">
                                <svg>
                                  <circle
                                    r="12"
                                    cx="50%"
                                    cy="50%"
                                    className="default"
                                  ></circle>
                                  <circle
                                    r="12"
                                    cx="50%"
                                    cy="50%"
                                    className="progress"
                                    stroke-dasharray="117"
                                    stroke-dashoffset="75"
                                  ></circle>
                                </svg>
                                <UserAvatar
                                  id={user?._id}
                                  className="position-absolute left-0 right-0 top-0 bottom-0 m-auto rounded-circle"
                                  alt=""
                                  title={user?.fullName}
                                  imageUrl={user?.photo?.location}
                                  image={user?.photo}
                                ></UserAvatar>
                              </div>
                            </div>
                            <div className="py-1">
                              <h6 className="mb-1">{user.fullName}</h6>
                              <p className="mb-0 font-xs text-gray-600 font-weight-semibold">
                                {user.designation?.name}{" "}
                                <div className="bg-gray-400 mx-2 top-0"></div>
                                {user.phones?.find((x) => x.isPrimary)?.phone}
                              </p>
                            </div>
                            <i
                              onClick={() => onSelectCustomer(user)}
                              className={classNames({
                                "icon-add icon-box border align-items-center border cursor-pointer d-flex icon-box justify-content-center ml-auto rounded-sm": true,
                                "icon-add": selectedCustomer?._id !== user._id,
                                "icon-close bg-blue text-white font-sm rounded":
                                  selectedCustomer?._id === user._id,
                              })}
                            ></i>
                          </li>
                        );
                      })}
                    </ul>
                    {showCustomerDropDown && (
                      <div className="custom-dropdown__menu custom-dropdown__menu--open border overflow-auto w-100 position-absolute left-0 bg-white zIndex-1">
                        {customers?.map((cust) => {
                          return (
                            <div
                              onClick={() => onSelectCustomer(cust)}
                              className="custom-dropdown__menu-item d-flex align-items-center cursor-pointer py-1 mb-1"
                            >
                              <div className="px-2">
                                <div className="font-sm font-weight-semibold">
                                  {cust.fullName}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        <div className="custom-dropdown__menu-item align-items-center cursor-pointer py-1 mb-1">
                          <div className="col-md-auto col-12 m-0 p-0">
                            <span
                              className="font-sm font-weight-semibold py-2 px-2 h-100 cursor-pointer text-primary"
                              onClick={() => props.setStage("create-company")}
                            >
                              <i className="icon-plus mr-1"></i> Add New
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="col-auto mt-md-0 mt-1 pl-0">
          <div className="d-inline-flex align-items-right flex-wrap mr-lg-5 mr-sm-3">
            {selectedCompnayId !== null && (
              <div className="bg-white mr-3   ">
                <button
                  onClick={onAddNewUser}
                  className="btn btn-outline-primary font-normal font-weight-bold"
                >
                  <i className="icon-plus mr-1"></i>Add New Contact
                </button>
              </div>
            )}

            <div className="mb-2 text-right">
              <button
                className="btn btn-primary font-normal font-weight-bold"
                disabled={!selectedCompany?._id || !containsId || isSubmiting}
                onClick={onNext}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchCompany;
